import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Signal,
  ViewChild,
  effect,
  inject,
  input,
} from '@angular/core';
import Chart, { ChartConfiguration, ChartConfigurationCustomTypesPerDataset } from 'chart.js/auto';
import 'chartjs-adapter-dayjs-4';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { getMinMaxDate } from '../../services/assessmentsHistory.functions';
import {
  AssessmentChartDataRow,
  AssessmentChartDataSets,
  AssessmentType,
  Patient,
  SubTestChartDataSet,
  assessmentTypeColors,
} from '../../services/model';
import { customPointCanvasLeft, customPointCanvasRight } from '../chart-utils/point-styles.functions';
import { PointStyleComponent } from '../chart-utils/point-style.component';
import { highlightPlugin, splitTextByMaxChars } from '../chart-utils/utils.functions';

@Component({
  selector: 'app-line-chart-nhpt',
  standalone: true,
  imports: [PointStyleComponent],
  templateUrl: './line-chart-nhpt.component.html',
  styleUrl: './line-chart-nhpt.component.scss',
})
export class LineChartNhptComponent implements OnInit {
  @Input() data!: Array<AssessmentChartDataRow> | undefined;
  @Input() dataFull!: Signal<AssessmentChartDataSets | undefined>;
  @Input() chartId!: string;
  @Input() patient!: Patient;
  selectedIndex = input(0);
  @Output() indexClicked = new EventEmitter<number>(true);
  @ViewChild('canvas', { read: ElementRef, static: true }) canvas!: ElementRef<any>;
  public chart: any;
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  constructor() {
    dayjs.locale('de');

    // getPersistedConfig()
    const subType: 'gesamt' | 'duebel' = 'gesamt';

    effect(() => {
      this.dataFull();
      if (this.chart) {
        this.createChart(subType);
        this.cdr.detectChanges();
        setTimeout(() => {
          // P.S. WTF!!!
          this.createChart(subType);
          this.cdr.detectChanges();
        }, 50);
      }
    });
    effect(() => {
      const newIndex = this.selectedIndex();
      // console.log('chriss effect' + newIndex);
      this.updateChart(newIndex);
    });
  }

  ngOnInit(): void {
    const subType: 'gesamt' | 'duebel' = 'gesamt';
    setTimeout(() => {
      this.createChart(subType);
      this.cdr.detectChanges();
    }, 50);
  }

  // Aktualisiere den Chart, wenn der Highlight-Index geändert wird
  updateChart(selectedIndex: number) {
    if (this.chart) {
      // console.log('chriss ' + selectedIndex);

      this.chart.options.plugins!.highlightPlugin!.highlightIndex = selectedIndex;
      this.chart.update();
    }
  }

  onSubTypeDropDownSelect(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value as 'gesamt' | 'duebel';
    console.log('Selected value:', selectedValue);
    this.createChart(selectedValue);
  }

  createChart(subType: 'gesamt' | 'duebel') {
    // jür jedes Datum eine Row
    const datasetsObject: AssessmentChartDataSets = this.dataFull() ?? {};
    const { minDate, maxDate, labelDataFull } = getMinMaxDate(datasetsObject);

    // data Array enthält für jeden Behandlungstermin ein Element, also eine Row
    // da steht dann immer der im Drop-Down selektierte subType score drin, je links und rechts
    const dataRows: Array<AssessmentChartDataRow> = this.data ?? [];
    const assesmentType: AssessmentType = dataRows[0]?.type ?? 'ARAT';
    const color = assessmentTypeColors[assesmentType];
    const data = dataRows.map(row => {
      let score_left = 0;
      let score_right = 0;
      let sum_left = 0;
      let sum_right = 0;
      let overtime_left = 0;
      let overtime_right = 0;
      let unitLabel_left = '';
      let unitLabel_right = '';
      let hasComment: boolean = false;
      let comment: string | undefined;

      // zieh die daten von dem nhpt subTest
      const subTest: SubTestChartDataSet = row.subDataSets[0];
      hasComment = row.hasComment || subTest.hasComment;
      comment = row.comment ?? subTest.comment;
      score_left = subTest.score_left!;
      score_right = subTest.score_right!;
      sum_left = subTest.sumLeft!;
      sum_right = subTest.sumRight!;
      overtime_left = subTest.extraLeft!;
      overtime_right = subTest.extraRight!;

      if (this.chartId === 'nhpt') {
        const norm_left = subTest.norm_left ?? 100;
        const norm_right = subTest.norm_right ?? 100;
        unitLabel_left = 'statt ' + Math.round(norm_left).toLocaleString('de-DE') + ' Sekunden';
        unitLabel_right = 'statt ' + Math.round(norm_right).toLocaleString('de-DE') + ' Sekunden';
      }

      // Invertierung Schädigung zu Erfüllung
      score_left = 100 - score_left;
      score_right = 100 - score_right;
      overtime_left = 100 - overtime_left;
      overtime_right = 100 - overtime_right;

      return {
        x: new Date(new Date(row.dateIso).toDateString()),
        dataL: score_left,
        dataR: score_right,
        hasComment,
        comment,
        overtimeL: score_left === 0 ? overtime_left : 100,
        overtimeR: score_right === 0 ? overtime_right : 100,
        ttovertimeL: (score_left === 0 ? overtime_left : 100) + ' % der Dübel (links)',
        ttovertimeR: (score_right === 0 ? overtime_right : 100) + ' % der Dübel (rechts)',
        tooltipLabelL: ` ${score_left} %  (${sum_left} ${unitLabel_left}) (links)`,
        tooltipLabelR: ` ${score_right} %  (${sum_right} ${unitLabel_right}) (rechts)`,
        documentId: row.documentId,
        assessmentType: row.type,
      };
    });

    // im multi-Line-chart haben wir immer nur 2 Datasets: eins für links und eins für rechts.
    // die liegen aber beide bereits in data. werden über den yAxisKey rausgefischt.
    const datasets = [
      {
        label: 'links' + (this.patient.affectedSide === 'l' || this.patient.affectedSide === 'b' ? ' (betroffen)' : ''),
        data: data,
        parsing: {
          yAxisKey: subType === 'gesamt' ? 'dataL' : 'overtimeL',
        },
        borderWidth: 2,
        tension: 0.2,
        borderColor: '#bde3ff',
        pointHitRadius: 20,
        pointStyle: customPointCanvasLeft,
        pointRadius: 10,
      },
      {
        label:
          'rechts' + (this.patient.affectedSide === 'r' || this.patient.affectedSide === 'b' ? ' (betroffen)' : ''),
        data: data,
        parsing: {
          yAxisKey: subType === 'gesamt' ? 'dataR' : 'overtimeR',
        },
        borderWidth: 2,
        tension: 0.1,
        borderColor: '#fcd19c',
        pointHitRadius: 20,
        pointStyle: customPointCanvasRight,
        pointRadius: 10,
      },
    ];

    const config = this.getChartConfig(datasets, minDate, maxDate, labelDataFull);

    // P.S. WTF!!! WTFFF
    // für später: selbst geprüft: das chart verschwindet vom scrren nach dem destroy. Die Daten stimmen!
    // Und doch wird genau der alte chart wieder gezeichnet. Der cached das irgendwie. Erst nochmal konplett zeichnen hilft!

    if (this.chart) {
      this.chart.destroy();
    }
    // if (this.chart) {
    //   this.chart.data = { labels: labelDataFull, datasets: datasets };
    //   this.chart.options.scales.x.min = minDate;
    //   this.chart.options.scales.x.max = maxDate;
    //   this.chart.update();
    // } else {
    this.chart = new Chart(this.canvas.nativeElement, config);
    setTimeout(() => {
      this.canvas.nativeElement.addEventListener('click', (e: any) => {
        const points = this.chart.getElementsAtEventForMode(e, 'nearest', {}, true);
        const index = points?.length > 0 ? points[0].index : -1;
        if (index > -1) {
          this.indexClicked.emit(index);
        }
      });
    }, 250);
    // }
  }

  getChartConfig(
    datasets: any,
    minDate: any,
    maxDate: any,
    labelDataFull: any
  ): ChartConfiguration | ChartConfigurationCustomTypesPerDataset {
    return {
      type: 'line',
      data: {
        labels: labelDataFull,
        datasets: datasets,
      },
      options: {
        interaction: {
          intersect: false,
          axis: 'xy',
          mode: 'index',
        },
        responsive: true,
        aspectRatio: 3,
        animation: {
          delay: 100,
        },
        clip: false,
        layout: {
          // autoPadding: true,
          //   padding: {
          //     left: -10,
          //     right: 10,
          //   },
        },
        scales: {
          y: {
            min: 0,
            max: 100,

            reverse: false,
            position: 'right',
            beginAtZero: true,
            grid: {
              display: true,
            },
            ticks: {
              stepSize: 25,
              callback: function (val, index) {
                return val + ' %';
              },
            },
          },
          x: {
            offset: true,
            grid: {
              display: true,
            },
            ticks: {
              source: 'data',
              // minRotation: 20,
              autoSkip: true, // Alle Datumswerte anzeigen
              callback: function (value, index, ticks) {
                // console.log('ticks:', { value, index, ticks });
                const tickDate = dayjs(new Date(value));
                // return tickDate.format('dd, DD.MM');
                const tick = [tickDate.format('DD.MM'), tickDate.format('dd')];
                const reverseIndex = datasets[0].data!.length - 1 - index;
                if (datasets[0].data[reverseIndex].hasComment) {
                  tick.push(`💬`);
                }
                return tick;
              },
            },
            type: 'time',
            min: minDate,
            max: maxDate,
            time: {
              unit: 'day',
              isoWeekday: true,
              displayFormats: {
                week: 'dd, DD.MM',
              },
              tooltipFormat: 'dd, DD.MM.YYYY',
            },
          },
        },
        plugins: {
          title: {
            display: false,
          },
          tooltip: {
            mode: 'index',
            bodyFont: {
              family: 'monospace',
              size: 12,
            },
            footerFont: {
              family: 'monospace',
              weight: 'normal',
              size: 12,
            },
            callbacks: {
              label: function (context) {
                const raw = context.raw as any;
                const yAxisKey = (context.dataset.parsing as any).yAxisKey;
                const tooltip = [];
                if (yAxisKey === 'dataL') {
                  tooltip.push(raw.tooltipLabelL);
                } else if (yAxisKey === 'dataR') {
                  tooltip.push(raw.tooltipLabelR);
                } else if (yAxisKey === 'overtimeL') {
                  tooltip.push(raw.ttovertimeL);
                } else if (yAxisKey === 'overtimeR') {
                  tooltip.push(raw.ttovertimeR);
                } else {
                  tooltip.push(raw.tooltipLabel);
                }
                return tooltip;
              },
              beforeFooter: function (context) {
                const raw = context[0].raw as any;
                const tooltip: string[] = [];
                if (raw.hasComment) {
                  tooltip.push('');
                  tooltip.push('💬 Kommentar zum Testergebnis');
                }

                return tooltip;
              },
              footer: function (context) {
                const raw = context[0].raw as any;
                if (raw.hasComment) {
                  return splitTextByMaxChars(raw.comment, 30);
                } else {
                  return [];
                }
              },
            },
          },
          legend: {
            display: false,
            position: 'top',
            align: 'start',
            labels: {
              usePointStyle: true,
            },
          },
          highlightPlugin: {
            highlightIndex: this.selectedIndex(), // Nutze die Komponente-Variable
            highlightWidth: 40,
            color: 'rgba(192, 192, 192, 0.2)',
          },
        },
      },
      plugins: [highlightPlugin],
    };
  }

  // nach Erfüllung (beste werte oben)
  drawChartBackgroundReverse(gradientBack: any) {
    gradientBack.addColorStop(0, 'rgba(251,221,221,1)');
    gradientBack.addColorStop(0.05, 'rgba(251,221,221,1)');

    gradientBack.addColorStop(0.05, 'rgba(255,228,200,1)');
    gradientBack.addColorStop(0.51, 'rgba(255,228,200,1)');

    gradientBack.addColorStop(0.51, 'rgba(252,244,219,1)');
    gradientBack.addColorStop(0.75, 'rgba(252,244,219,1)');

    gradientBack.addColorStop(0.75, 'rgba(252,252,239,1)');
    gradientBack.addColorStop(0.96, 'rgba(252,252,239,1)');

    gradientBack.addColorStop(0.96, 'rgba(255,255,255,1)');
    gradientBack.addColorStop(1, 'rgba(255,255,255,1)');
  }

  customCanvasBackgroundColor = {
    id: 'customCanvasBackgroundColor',
    beforeDraw: (chart: any, args: any, options: any) => {
      const {
        ctx,
        chartArea: { left, right, top, bottom, width, height },
      } = chart;
      const canvas = chart.canvas;
      const chartArea = chart.chartArea;

      ctx.save();
      // Chart background
      const gradientBack = canvas.getContext('2d').createLinearGradient(right, bottom, right, top);
      this.drawChartBackgroundReverse(gradientBack);
      ctx.fillStyle = gradientBack;
      ctx.fillRect(
        chartArea.left,
        chartArea.bottom,
        chartArea.right - chartArea.left,
        chartArea.top - chartArea.bottom
      );
    },
  };
}
